import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Switch } from '@qh/shared';
import { useMasterData } from '../../context/MasterDataContext';
import { candidateApi, type MasterDataRef } from '../../lib/apiClient';
import {
  profileFieldDomId,
  validateFullName,
  type ProfileFieldErrors,
  type ProfileFieldKey,
} from '../../lib/profileValidation';

// Re-export so existing imports from this module keep working after the
// validator was lifted to `lib/profileValidation.ts` for cross-page reuse
// (signup form also consumes it now). Pure move — semantics unchanged.
export { validateFullName };
import type {
  EducationEntry,
  ProfileFormState,
  WorkEntry,
} from './profileFormShape';
import { DeclarationFields } from './DeclarationFields';

/** Phone — digits/spaces/`+ - ( ) /`, ≥10 digits, ≤20 chars. Required. */
export function validatePhone(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Phone number is required';
  if (trimmed.length > 20) return 'Phone number must be less than 20 characters';
  const digitCount = (trimmed.match(/\d/g) ?? []).length;
  if (digitCount < 10) return 'Phone number must contain at least 10 digits';
  if (!/^[\d\s+\-()/]+$/.test(trimmed)) {
    return 'Phone number can only contain digits, spaces, and + - ( ) /';
  }
  return null;
}

/**
 * Returns the first ({field, message}) for the given form, or null if everything is valid.
 * Used by parent forms (MyProfilePage, ProfileCompletionModal) to gate save and scroll
 * the user to the first invalid field. Field names map to DOM ids set on the wrappers.
 */
export function validateProfileForm(
  form: ProfileFormState,
): { field: 'full_name' | 'phone' | 'fraud_declaration'; message: string } | null {
  const nameErr = validateFullName(form.full_name);
  if (nameErr) return { field: 'full_name', message: nameErr };
  const phoneErr = validatePhone(form.phone);
  if (phoneErr) return { field: 'phone', message: phoneErr };
  // Fraud declaration is mandatory — Yes only. Backend mirrors this guard, but
  // catching it here lets us scroll to the field instead of relying on a generic
  // top-level error from a 400 response.
  if (form.fraud_declaration !== 'yes') {
    return {
      field: 'fraud_declaration',
      message: 'Please agree to the Fraud Declaration to save your profile.',
    };
  }
  return null;
}

/** ID attached to the wrapper around each validated field, so a parent can scroll to it. */
export function profileFieldId(field: 'full_name' | 'phone' | 'fraud_declaration'): string {
  return `profile-field-${field}`;
}

/**
 * The seven section cards that make up the candidate profile form. Used by both
 * `ProfileCompletionModal` (Step 2) and `MyProfilePage`. Sections + field set
 * mirror analysis §5.3.
 *
 * Stateless w.r.t. persistence — the parent owns `form` state and `setForm`. The
 * skill picker DOES manage its own search query/results internally because that's
 * a pure UI concern.
 */

export interface ProfileFormSectionsProps {
  form: ProfileFormState;
  setForm: (next: ProfileFormState) => void;
  /** Read-only display fields (locked at signup). */
  email: string;
  regionName: string;
  /** Pre-resolved skill catalog (matched chips from a parse, plus base catalog).
   *  The form merges in any newly-added skills as the user picks them. */
  initialSkillCatalog?: MasterDataRef[];
  /** Names from the AI parse that didn't resolve to catalog skills, surfaced as a
   *  dismissible note above the picker. Optional. */
  unmatchedSkillNames?: string[];
  onDismissUnmatchedSkill?: (name: string) => void;
  /** Backend field-level validation errors, keyed by form field. */
  fieldErrors?: ProfileFieldErrors;
  /** Notified when the user edits a field — parent uses this to clear the stale
   *  field error so the inline message disappears as soon as they start fixing it. */
  onFieldChange?: (key: ProfileFieldKey) => void;
  /** Declaration consent timestamps (read-only display under each radio group).
   *  Both optional — undefined / null means "never consented", and the inline
   *  caption is omitted. New candidates (ProfileCompletionModal) pass neither. */
  rtrConsentAt?: string | null;
  fraudConsentAt?: string | null;
}

export function ProfileFormSections({
  form,
  setForm,
  email,
  regionName,
  initialSkillCatalog,
  unmatchedSkillNames,
  onDismissUnmatchedSkill,
  fieldErrors,
  onFieldChange,
  rtrConsentAt,
  fraudConsentAt,
}: ProfileFormSectionsProps) {
  const { data: master } = useMasterData();
  const [fullNameError, setFullNameError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  function update<K extends keyof ProfileFormState>(key: K, value: ProfileFormState[K]) {
    setForm({ ...form, [key]: value });
    // Clear inline error as the user types so stale errors don't linger.
    if (key === 'full_name' && fullNameError) setFullNameError(null);
    if (key === 'phone' && phoneError) setPhoneError(null);
    onFieldChange?.(key as ProfileFieldKey);
  }

  /** Pull the message for a given field from the backend error map. */
  const fe = (key: ProfileFieldKey): string | undefined => fieldErrors?.[key];

  function updateWork(index: number, patch: Partial<WorkEntry>) {
    const next = [...form.work_history];
    next[index] = { ...next[index], ...patch };
    update('work_history', next);
  }

  function removeWork(index: number) {
    update(
      'work_history',
      form.work_history.filter((_, i) => i !== index),
    );
  }

  function addWork() {
    update('work_history', [
      ...form.work_history,
      { title: '', company: '', start_date: '', end_date: '', description: '' },
    ]);
  }

  function updateEducation(index: number, patch: Partial<EducationEntry>) {
    const next = [...form.education];
    next[index] = { ...next[index], ...patch };
    update('education', next);
  }

  function removeEducation(index: number) {
    update(
      'education',
      form.education.filter((_, i) => i !== index),
    );
  }

  function addEducation() {
    update('education', [
      ...form.education,
      { degree: '', school: '', field: '', start_date: '', end_date: '' },
    ]);
  }

  return (
    <div className="space-y-5">
      <SectionCard title="Personal">
        <div id={profileFieldId('full_name')}>
          <Field label="Full name" required>
            <TextInput
              value={form.full_name}
              onChange={(v) => update('full_name', v)}
              onBlur={(v) => setFullNameError(validateFullName(v))}
              autoComplete="name"
              invalid={Boolean(fullNameError)}
            />
          </Field>
          {fullNameError && (
            <p className="text-xs text-red-600 font-medium mt-1">{fullNameError}</p>
          )}
        </div>
        <FieldRow>
          <Field label="Email">
            <TextInput value={email} disabled />
          </Field>
          <Field label="Region">
            <TextInput value={regionName} disabled />
          </Field>
        </FieldRow>
        <div id={profileFieldId('phone')}>
          <Field label="Phone" required>
            <TextInput
              value={form.phone}
              onChange={(v) => update('phone', v)}
              onBlur={(v) => setPhoneError(validatePhone(v))}
              autoComplete="tel"
              inputMode="tel"
              invalid={Boolean(phoneError)}
            />
          </Field>
          {phoneError && (
            <p className="text-xs text-red-600 font-medium mt-1">{phoneError}</p>
          )}
        </div>
        <div id={profileFieldDomId('linkedin_url')}>
          <Field label="LinkedIn URL">
            <TextInput
              value={form.linkedin_url}
              onChange={(v) => update('linkedin_url', v)}
              placeholder="https://linkedin.com/in/…"
              autoComplete="url"
              error={fe('linkedin_url')}
            />
          </Field>
        </div>
        <div id={profileFieldDomId('github_url')}>
          <Field label="GitHub URL">
            <TextInput
              value={form.github_url}
              onChange={(v) => update('github_url', v)}
              placeholder="https://github.com/…"
              autoComplete="url"
              error={fe('github_url')}
            />
          </Field>
        </div>
        <div id={profileFieldDomId('portfolio_url')}>
          <Field label="Portfolio URL">
            <TextInput
              value={form.portfolio_url}
              onChange={(v) => update('portfolio_url', v)}
              placeholder="https://…"
              autoComplete="url"
              error={fe('portfolio_url')}
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Current Position">
        <div id={profileFieldDomId('current_role')}>
          <Field label="Current title">
            <TextInput
              value={form.current_role}
              onChange={(v) => update('current_role', v)}
              error={fe('current_role')}
            />
          </Field>
        </div>
        <div id={profileFieldDomId('current_company')}>
          <Field label="Current company">
            <TextInput
              value={form.current_company}
              onChange={(v) => update('current_company', v)}
              error={fe('current_company')}
            />
          </Field>
        </div>
        <FieldRow>
          <div id={profileFieldDomId('years_experience')}>
            <Field label="Years experience">
              <TextInput
                value={form.years_experience}
                onChange={(v) => update('years_experience', v.replace(/[^0-9.]/g, ''))}
                inputMode="numeric"
                placeholder="e.g. 5"
                error={fe('years_experience')}
              />
            </Field>
          </div>
          <Field label="Experience level">
            <SelectInput
              value={form.experience_level_id}
              onChange={(v) => update('experience_level_id', v)}
              options={master?.experienceLevels ?? []}
              placeholder="Select…"
            />
          </Field>
        </FieldRow>
      </SectionCard>

      <SectionCard title="Summary">
        <div id={profileFieldDomId('summary')}>
          <textarea
            value={form.summary}
            onChange={(e) => update('summary', e.target.value)}
            rows={4}
            placeholder="A short narrative of your experience and what you're looking for next."
            className={`w-full rounded-2xl border bg-white px-4 py-3 text-sm text-brand-primary font-medium placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 ${fe('summary') ? 'border-red-400 focus:ring-red-300' : 'border-gray-200 focus:ring-brand-accent'}`}
          />
          {fe('summary') && (
            <p className="text-xs text-red-600 font-medium mt-1">{fe('summary')}</p>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Work History">
        {form.work_history.length === 0 ? (
          <p className="text-sm text-brand-muted">
            No work entries yet. Add roles you've held to give companies a richer view of your background.
          </p>
        ) : (
          <div className="space-y-4">
            {form.work_history.map((entry, idx) => (
              <RepeatingCard key={idx} onRemove={() => removeWork(idx)} label={`Work entry ${idx + 1}`}>
                <FieldRow>
                  <Field label="Title">
                    <TextInput
                      value={entry.title}
                      onChange={(v) => updateWork(idx, { title: v })}
                    />
                  </Field>
                  <Field label="Company">
                    <TextInput
                      value={entry.company}
                      onChange={(v) => updateWork(idx, { company: v })}
                    />
                  </Field>
                </FieldRow>
                <FieldRow>
                  <Field label="Start (YYYY-MM)">
                    <TextInput
                      value={entry.start_date}
                      onChange={(v) => updateWork(idx, { start_date: v })}
                      placeholder="2022-01"
                    />
                  </Field>
                  <Field label="End (YYYY-MM or 'Present')">
                    <TextInput
                      value={entry.end_date}
                      onChange={(v) => updateWork(idx, { end_date: v })}
                      placeholder="Present"
                    />
                  </Field>
                </FieldRow>
                <Field label="Description">
                  <textarea
                    value={entry.description}
                    onChange={(e) => updateWork(idx, { description: e.target.value })}
                    rows={3}
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-brand-primary font-medium placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-accent"
                  />
                </Field>
              </RepeatingCard>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={addWork}
          className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-dashed border-brand-muted/50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-primary hover:border-brand-primary hover:bg-gray-50 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add work entry
        </button>
      </SectionCard>

      <SectionCard title="Education">
        {form.education.length === 0 ? (
          <p className="text-sm text-brand-muted">No education entries yet.</p>
        ) : (
          <div className="space-y-4">
            {form.education.map((entry, idx) => (
              <RepeatingCard
                key={idx}
                onRemove={() => removeEducation(idx)}
                label={`Education entry ${idx + 1}`}
              >
                <FieldRow>
                  <Field label="Degree">
                    <TextInput
                      value={entry.degree}
                      onChange={(v) => updateEducation(idx, { degree: v })}
                    />
                  </Field>
                  <Field label="Field">
                    <TextInput
                      value={entry.field}
                      onChange={(v) => updateEducation(idx, { field: v })}
                    />
                  </Field>
                </FieldRow>
                <Field label="School">
                  <TextInput
                    value={entry.school}
                    onChange={(v) => updateEducation(idx, { school: v })}
                  />
                </Field>
                <FieldRow>
                  <Field label="Start (YYYY-MM)">
                    <TextInput
                      value={entry.start_date}
                      onChange={(v) => updateEducation(idx, { start_date: v })}
                    />
                  </Field>
                  <Field label="End (YYYY-MM)">
                    <TextInput
                      value={entry.end_date}
                      onChange={(v) => updateEducation(idx, { end_date: v })}
                    />
                  </Field>
                </FieldRow>
              </RepeatingCard>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={addEducation}
          className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-dashed border-brand-muted/50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-primary hover:border-brand-primary hover:bg-gray-50 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add education entry
        </button>
      </SectionCard>

      <SectionCard title="Skills">
        <SkillPickerField
          selectedIds={form.skill_ids}
          setSelectedIds={(ids) => update('skill_ids', ids)}
          initialCatalog={initialSkillCatalog}
          unmatchedNames={unmatchedSkillNames}
          onDismissUnmatched={onDismissUnmatchedSkill}
        />
        <Field label="Soft skills">
          <TextInput
            value={form.soft_skills}
            onChange={(v) => update('soft_skills', v)}
            placeholder="Communication, leadership, …"
          />
          <Helper>Comma-separated</Helper>
        </Field>
        <Field label="Tools">
          <TextInput
            value={form.tools}
            onChange={(v) => update('tools', v)}
            placeholder="Figma, Jira, …"
          />
          <Helper>Comma-separated</Helper>
        </Field>
        <Field label="Certifications">
          <TextInput
            value={form.certifications}
            onChange={(v) => update('certifications', v)}
            placeholder="AWS Certified Solutions Architect, …"
          />
          <Helper>Comma-separated</Helper>
        </Field>
      </SectionCard>

      <SectionCard title="Job Preferences">
        <FieldRow>
          <div id={profileFieldDomId('expected_salary_min')}>
            <Field label="Expected salary min">
              <TextInput
                value={form.expected_salary_min}
                onChange={(v) => update('expected_salary_min', v.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                error={fe('expected_salary_min')}
              />
            </Field>
          </div>
          <div id={profileFieldDomId('expected_salary_max')}>
            <Field label="Expected salary max">
              <TextInput
                value={form.expected_salary_max}
                onChange={(v) => update('expected_salary_max', v.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                error={fe('expected_salary_max')}
              />
            </Field>
          </div>
        </FieldRow>
        <FieldRow>
          <Field label="Currency">
            <SelectInput
              value={form.expected_salary_currency_id}
              onChange={(v) => update('expected_salary_currency_id', v)}
              options={(master?.currencies ?? []).map((c) => ({
                id: c.id,
                slug: c.slug,
                name: `${c.code} (${c.symbol})`,
              }))}
              placeholder="Select…"
            />
          </Field>
          <Field label="Pay period">
            <SelectInput
              value={form.expected_salary_pay_period_id}
              onChange={(v) => update('expected_salary_pay_period_id', v)}
              options={master?.payPeriods ?? []}
              placeholder="Select…"
            />
          </Field>
        </FieldRow>
        <Field label="Notice period">
          <SelectInput
            value={form.notice_period_id}
            onChange={(v) => update('notice_period_id', v)}
            options={master?.noticePeriods ?? []}
            placeholder="Select…"
          />
        </Field>
        <div className="mt-2">
          <Switch
            checked={form.willing_to_relocate}
            onChange={(v) => update('willing_to_relocate', v)}
            label="Willing to relocate"
            description="Lets companies know you're open to opportunities outside your current city."
          />
        </div>
      </SectionCard>

      <SectionCard title="Declaration">
        {/* Wording and radios live in DeclarationFields (shared with the invite claim form and
            the apply declaration popup — recruitment workflow §6.3 / §6.4). */}
        <DeclarationFields
          rtr={form.rtr_consent}
          fraud={form.fraud_declaration}
          onRtrChange={(v) => update('rtr_consent', v)}
          onFraudChange={(v) => update('fraud_declaration', v)}
          rtrConsentAt={rtrConsentAt}
          fraudConsentAt={fraudConsentAt}
          fraudWrapperId={profileFieldId('fraud_declaration')}
        />
      </SectionCard>
    </div>
  );
}

/** Skill picker — debounced server search + chip list. */
function SkillPickerField({
  selectedIds,
  setSelectedIds,
  initialCatalog,
  unmatchedNames,
  onDismissUnmatched,
}: {
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  initialCatalog?: MasterDataRef[];
  unmatchedNames?: string[];
  onDismissUnmatched?: (name: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MasterDataRef[]>([]);
  const [catalog, setCatalog] = useState<MasterDataRef[]>(initialCatalog ?? []);
  const debounceRef = useRef<number | null>(null);

  // Merge any new entries from `initialCatalog` into the local catalog. Lets the
  // parent (MyProfilePage) push freshly-hydrated skills after a re-parse so chips
  // can render names for matched_skill_ids that weren't in the bootstrap top-50.
  useEffect(() => {
    if (!initialCatalog || initialCatalog.length === 0) return;
    setCatalog((prev) => {
      const seen = new Set(prev.map((s) => s.id));
      const additions = initialCatalog.filter((s) => !seen.has(s.id));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, [initialCatalog]);

  useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      candidateApi
        .publicSkills(q)
        .then((rows) => {
          setResults(rows);
          // Merge into the local catalog so chip names resolve.
          setCatalog((prev) => {
            const seen = new Set(prev.map((s) => s.id));
            const merged = [...prev];
            for (const r of rows) if (!seen.has(r.id)) merged.push(r);
            return merged;
          });
        })
        .catch(() => setResults([]));
    }, 250);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of catalog) map.set(s.id, s.name);
    return map;
  }, [catalog]);

  function add(id: string) {
    if (selectedIds.includes(id)) return;
    setSelectedIds([...selectedIds, id]);
    setQuery('');
    setResults([]);
  }

  function remove(id: string) {
    setSelectedIds(selectedIds.filter((s) => s !== id));
  }

  return (
    <div>
      <p className="text-xs font-black uppercase tracking-[0.18em] text-brand-muted mb-2">
        Tech skills
      </p>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-secondary px-3 py-1 text-xs font-bold text-brand-deep"
            >
              {nameById.get(id) ?? id}
              <button
                type="button"
                aria-label="Remove"
                onClick={() => remove(id)}
                className="text-brand-deep/60 hover:text-brand-deep"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {unmatchedNames && unmatchedNames.length > 0 && (
        <div className="mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p className="font-bold mb-1">Unmatched skills from your resume</p>
          <p className="mb-2">These didn't match our catalog. Add similar ones manually if needed:</p>
          <div className="flex flex-wrap gap-1.5">
            {unmatchedNames.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 border border-amber-200"
              >
                {name}
                {onDismissUnmatched && (
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => onDismissUnmatched(name)}
                    className="text-amber-700 hover:text-amber-900"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search skills (e.g. React, Python)…"
          className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-brand-primary font-medium placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-accent"
        />
        {results.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-lg">
            {results.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => add(r.id)}
                  className="block w-full text-left px-4 py-2 text-sm font-medium text-brand-primary hover:bg-brand-secondary/40"
                >
                  {r.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Layout primitives (local) ----------

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 sm:p-6">
      <h2 className="text-sm font-black uppercase tracking-[0.18em] text-brand-primary mb-4">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function RepeatingCard({
  children,
  onRemove,
  label,
}: {
  children: React.ReactNode;
  onRemove: () => void;
  label: string;
}) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-3 relative">
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="absolute top-3 right-3 p-1 text-brand-muted hover:text-red-600 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-black uppercase tracking-[0.18em] text-brand-muted mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Helper({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] text-brand-muted mt-1">{children}</p>;
}

function TextInput({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  inputMode,
  autoComplete,
  invalid,
  error,
}: {
  value: string;
  onChange?: (v: string) => void;
  onBlur?: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  inputMode?: 'text' | 'numeric' | 'tel' | 'url' | 'email';
  autoComplete?: string;
  invalid?: boolean;
  /** Optional error message rendered below the input. When set, the input is
   *  also styled as invalid even if `invalid` isn't passed. */
  error?: string | null;
}) {
  const isInvalid = invalid || Boolean(error);
  return (
    <>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={(e) => onBlur?.(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        inputMode={inputMode}
        autoComplete={autoComplete}
        className={`w-full rounded-2xl border bg-white px-4 py-2.5 text-sm text-brand-primary font-medium placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-brand-muted focus:border-transparent focus:outline-none focus:ring-2 ${isInvalid ? 'border-red-400 focus:ring-red-300' : 'border-gray-200 focus:ring-brand-accent'}`}
      />
      {error && <p className="text-xs text-red-600 font-medium mt-1">{error}</p>}
    </>
  );
}

function SelectInput({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ id: string; slug: string; name: string }>;
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-brand-primary font-medium focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-accent"
    >
      <option value="">{placeholder ?? 'Select…'}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );
}
